import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { listItems, listSuppliers, listCategories, InventoryItem, Supplier, Category } from './api';
import './inventory-v2.css';
import DataTable from '../../shared/components/DataTable';

const ItemList: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState(searchParams.get('search') || '');

  const filters = useMemo(() => ({
    search: searchParams.get('search') || undefined,
    supplier_id: searchParams.get('supplier_id') ? Number(searchParams.get('supplier_id')) : undefined,
    category_id: searchParams.get('category_id') ? Number(searchParams.get('category_id')) : undefined,
    status: searchParams.get('status') || undefined,
    low_stock: searchParams.get('low_stock') === 'true' || undefined,
    include_inactive: searchParams.get('include_inactive') === 'true' || undefined,
  }), [searchParams]);

  const loadData = useCallback(() => {
    Promise.all([
      listItems(sessionToken, filters),
      listSuppliers(sessionToken),
      listCategories(sessionToken),
    ])
      .then(([itemsRes, suppliersRes, catsRes]) => {
        setItems(itemsRes.items);
        setSuppliers(suppliersRes.suppliers);
        setCategories(catsRes.categories);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [sessionToken, filters]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    setSearchParams(params);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateFilter('search', search);
  };

  if (loading) return <div className="inv2-loading">Loading items...</div>;
  if (error) return <div className="inv2-error">{error}</div>;

  return (
    <div className="inv2-page">
      <div className="inv2-header">
        <h1>📦 Inventory Items</h1>
        <div className="inv2-header-actions">
          <Link to="/inventory/import" className="inv2-btn">📥 Import</Link>
          {['Owner', 'Admin', 'Manager'].includes(user?.role || '') && (
            <Link to="/inventory/items/new" className="inv2-btn inv2-btn-primary">+ Add Item</Link>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="inv2-filters">
        <form onSubmit={handleSearch} className="inv2-search-form">
          <input
            type="text"
            placeholder="Search items..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="inv2-input"
          />
          <button type="submit" className="inv2-btn">Search</button>
        </form>
        <select
          value={filters.supplier_id || ''}
          onChange={e => updateFilter('supplier_id', e.target.value)}
          className="inv2-select"
        >
          <option value="">All Suppliers</option>
          {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select
          value={filters.category_id || ''}
          onChange={e => updateFilter('category_id', e.target.value)}
          className="inv2-select"
        >
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select
          value={filters.status || ''}
          onChange={e => updateFilter('status', e.target.value)}
          className="inv2-select"
        >
          <option value="">All Status</option>
          <option value="low">Low</option>
          <option value="moderate">Moderate</option>
          <option value="high">High</option>
          <option value="out-of-stock">Out of Stock</option>
        </select>
        <label className="inv2-checkbox-label">
          <input
            type="checkbox"
            checked={filters.low_stock || false}
            onChange={e => updateFilter('low_stock', e.target.checked ? 'true' : '')}
          />
          Low Stock Only
        </label>
      </div>

      {/* Results count */}
      <p className="inv2-count">{items.length} item{items.length !== 1 ? 's' : ''} found</p>

      {/* Table */}
      <div className="inv2-table-container">
        <DataTable
          rows={items}
          rowKey={(item) => item.id}
          caption="Inventory items"
          moreLabel="Pricing & levels"
          empty="No items found"
          rowClass={(item) => (!item.is_active ? 'inv2-row-inactive' : undefined)}
          columns={[
            {
              key: 'name', header: 'Item Name', priority: 'primary',
              render: (item) => (
                <>
                  <Link to={`/inventory/items/${item.id}`} className="inv2-link">
                    {item.name}
                    {item.brand && <span className="inv2-brand"> ({item.brand})</span>}
                  </Link>
                  {!item.is_active && <span className="inv2-badge-inactive">Inactive</span>}
                </>
              ),
            },
            {
              key: 'onhand', header: 'On Hand', priority: 'summary', numeric: true,
              render: (item) => (
                <span className={item.status === 'low' || item.status === 'out-of-stock' ? 'inv2-text-danger' : undefined}>
                  {item.stock_known === false ? 'Not counted' : `${Number(item.on_hand_quantity).toFixed(1)} ${item.base_unit}`}
                </span>
              ),
            },
            { key: 'status', header: 'Status', priority: 'summary', render: (item) => <span className={`inv2-status inv2-status-${item.status}`}>{item.stock_known===false?'Count needed':item.status}</span> },
            { key: 'supplier', header: 'Supplier', priority: 'summary', render: (item) => item.supplier_name || '—' },
            { key: 'category', header: 'Category', priority: 'summary', render: (item) => item.category_name || '—' },
            { key: 'unit', header: 'Unit', priority: 'detail', render: (item) => item.base_unit },
            { key: 'threshold', header: 'Threshold', priority: 'detail', numeric: true, render: (item) => item.threshold_quantity ?? '—' },
            { key: 'par', header: 'Par Level', priority: 'detail', numeric: true, render: (item) => item.par_level ?? '—' },
            { key: 'price', header: 'Price/Unit', priority: 'detail', numeric: true, render: (item) => (item.last_received_unit_price != null ? `$${Number(item.last_received_unit_price).toFixed(4)}` : '—') },
            { key: 'avg', header: 'Avg Price', priority: 'detail', numeric: true, render: (item) => (item.weighted_average_unit_price != null ? `$${Number(item.weighted_average_unit_price).toFixed(4)}` : '—') },
            { key: 'received', header: 'Last Received', priority: 'detail', render: (item) => (item.last_received_at ? new Date(item.last_received_at).toLocaleDateString() : '—') },
          ]}
        />
      </div>
    </div>
  );
};

export default ItemList;
